import React, { useState } from "react";
import { Link, NavLink, Navigate, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Table2,
  Download,
  Settings,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/diagnosticos", label: "Diagnósticos", icon: Table2 },
  { to: "/admin/exportacoes", label: "Exportações", icon: Download },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings },
];

export default function AdminLayout({ children }) {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">
        Verificando sessão…
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] flex">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky top-0 h-screen z-40 w-72 bg-[#0F172A] text-white flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 flex`}
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <img
              src="/brand/hub-logo.webp?v=2"
              alt="HUB"
              className="h-10 w-auto rounded-md"
            />
            <div className="leading-tight">
              <div className="font-display font-medium text-sm">Painel HUB</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D97706] font-semibold">
                Administrativo
              </div>
            </div>
          </Link>
          <button
            className="lg:hidden text-white/80"
            onClick={() => setOpen(false)}
            data-testid="admin-sidebar-close"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-white text-[#0F172A] font-medium"
                      : "text-slate-300 hover:bg-white/5"
                  }`
                }
              >
                <Icon size={16} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-slate-400 mb-3 truncate" title={admin.email}>
            {admin.email}
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-2 text-sm text-slate-300 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            <LogOut size={16} /> Sair
          </button>
        </div>
      </aside>

      {/* Overlay on mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 h-14 flex items-center px-4 justify-between">
          <button
            data-testid="admin-menu-btn"
            onClick={() => setOpen(true)}
            className="p-2 -ml-2 text-slate-700"
          >
            <Menu size={20} />
          </button>
          <div className="font-display font-medium text-sm">Painel HUB</div>
          <div className="w-8" />
        </header>
        <main className="px-5 sm:px-8 lg:px-10 py-8 lg:py-10 max-w-7xl">{children}</main>
      </div>
    </div>
  );
}
